import React, { useState } from 'react'
import { useAuth } from '../context/AuthContext.jsx'

export default function UploadPage() {
  const { user, tokens } = useAuth()
  const [file, setFile] = useState(null)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [visibility, setVisibility] = useState('public') // public | registered | private
  const [privateUserId, setPrivateUserId] = useState('')
  const [resolutions, setResolutions] = useState(['720p','480p','360p'])
  const [tags, setTags] = useState({ grade: '', category: '', author: '' })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  if (!user || user.role !== 'superadmin') {
    return (
      <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
        <div><h2>Access denied</h2><p>Superadmin required.</p></div>
      </div>
    )
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    setError(''); setMessage(''); setSubmitting(true)
    try {
      if (!file) throw new Error('Select a video file')
      if (!title.trim()) throw new Error('Enter a title')
      if (visibility === 'private' && !privateUserId.trim()) throw new Error('Private user ID is required for private visibility')
      if (!resolutions.length) throw new Error('Select at least one resolution')

      // Prepare multipart form-data
      const form = new FormData()
      form.append('video', file) // backend expects field name 'video'
      form.append('title', title)
      form.append('description', description)
      form.append('visibility', visibility) // public | registered | private
      if (visibility === 'private') form.append('privateUserId', privateUserId)
      form.append('resolutions', JSON.stringify(resolutions))

      const res = await fetch('/api/admin/videos/upload', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${tokens?.accessToken || ''}`,
        },
        body: form,
      })

      if (!res.ok) throw new Error((await res.json().catch(()=>({message:'Upload failed'}))).message || 'Upload failed')
      
      const data = await res.json()
      const videoId = data.data?.video?.id

      // Add tags if any are provided
      if (videoId && (tags.grade || tags.category || tags.author)) {
        const tagArray = []
        if (tags.grade) tagArray.push({ tagType: 'grade', tagValue: tags.grade })
        if (tags.category) tagArray.push({ tagType: 'category', tagValue: tags.category })
        if (tags.author) tagArray.push({ tagType: 'author', tagValue: tags.author })

        await fetch(`/api/admin/videos/${videoId}/tags/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${tokens?.accessToken || ''}`
          },
          body: JSON.stringify({ tags: tagArray })
        })
      }

      setMessage('Upload started. Transcoding (720p, 480p, 360p) will run in the background.')
      setFile(null)
      setTitle(''); setDescription(''); setVisibility('public'); setPrivateUserId(''); setResolutions(['720p','480p','360p']); setTags({ grade: '', category: '', author: '' })
    } catch (err) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={{ maxWidth: 680, margin: '32px auto', padding: '0 20px' }}>
      <h1 style={{ marginBottom: 24, fontSize: 32, fontWeight: 600 }}>Upload Video</h1>
      <form onSubmit={onSubmit} style={{ display:'grid', gap:24, background:'var(--bg)', border:'1px solid var(--elev)', borderRadius:16, padding:24 }}>
        
        <div style={{ display:'grid', gap:20 }}>
          <label style={{ display:'grid', gap:8 }}>
            <span style={{ fontWeight:500, fontSize:14 }}>Title</span>
            <input 
              type="text" 
              value={title} 
              onChange={(e)=>setTitle(e.target.value)} 
              placeholder="Enter video title" 
              required 
              style={{ padding:'10px 12px', borderRadius:8, border:'1px solid var(--elev)', fontSize:15 }}
            />
          </label>

          <label style={{ display:'grid', gap:8 }}>
            <span style={{ fontWeight:500, fontSize:14 }}>Description</span>
            <textarea 
              value={description} 
              onChange={(e)=>setDescription(e.target.value)} 
              placeholder="Add a description (optional)" 
              rows={4}
              style={{ padding:'10px 12px', borderRadius:8, border:'1px solid var(--elev)', fontSize:15, resize:'vertical', fontFamily:'inherit' }}
            />
          </label>

          <label style={{ display:'grid', gap:8 }}>
            <span style={{ fontWeight:500, fontSize:14 }}>Video file</span>
            <div style={{ position:'relative' }}>
              <input 
                type="file" 
                accept="video/*" 
                onChange={(e)=>setFile(e.target.files?.[0]||null)}
                style={{ padding:'10px 12px', borderRadius:8, border:'1px solid var(--elev)', width:'100%', fontSize:14 }}
              />
              {file && <div style={{ marginTop:8, fontSize:13, color:'var(--muted)' }}>Selected: {file.name}</div>}
            </div>
          </label>
        </div>

        <fieldset style={{ border:'1px solid var(--elev)', borderRadius:12, padding:16, display:'grid', gap:12 }}>
          <legend style={{ fontWeight:500, fontSize:14, padding:'0 8px' }}>Transcode resolutions</legend>
          <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
            {['720p','480p','360p'].map(key => (
              <label key={key} style={{ display:'flex', gap:8, alignItems:'center', cursor:'pointer', padding:'6px 12px', borderRadius:8, background: resolutions.includes(key) ? 'color-mix(in oklab, var(--bg), var(--fg) 8%)' : 'transparent', transition:'background 0.15s' }}>
                <input 
                  type="checkbox" 
                  checked={resolutions.includes(key)} 
                  onChange={()=>setResolutions(prev => prev.includes(key) ? prev.filter(k=>k!==key) : [...prev, key])}
                  style={{ cursor:'pointer' }}
                />
                <span style={{ fontSize:14, fontWeight:500 }}>{key}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset style={{ border:'1px solid var(--elev)', borderRadius:12, padding:16, display:'grid', gap:12 }}>
          <legend style={{ fontWeight:500, fontSize:14, padding:'0 8px' }}>Visibility</legend>
          <div style={{ display:'grid', gap:10 }}>
            <label style={{ display:'flex', gap:10, alignItems:'center', cursor:'pointer', padding:'8px 12px', borderRadius:8, background: visibility==='public' ? 'color-mix(in oklab, var(--bg), var(--fg) 8%)' : 'transparent', transition:'background 0.15s' }}>
              <input type="radio" name="visibility" value="public" checked={visibility==='public'} onChange={()=>setVisibility('public')} style={{ cursor:'pointer' }} />
              <span style={{ fontSize:14 }}>Public</span>
            </label>
            <label style={{ display:'flex', gap:10, alignItems:'center', cursor:'pointer', padding:'8px 12px', borderRadius:8, background: visibility==='registered' ? 'color-mix(in oklab, var(--bg), var(--fg) 8%)' : 'transparent', transition:'background 0.15s' }}>
              <input type="radio" name="visibility" value="registered" checked={visibility==='registered'} onChange={()=>setVisibility('registered')} style={{ cursor:'pointer' }} />
              <span style={{ fontSize:14 }}>Registered users</span>
            </label>
            <label style={{ display:'flex', gap:10, alignItems:'center', cursor:'pointer', padding:'8px 12px', borderRadius:8, background: visibility==='private' ? 'color-mix(in oklab, var(--bg), var(--fg) 8%)' : 'transparent', transition:'background 0.15s' }}>
              <input type="radio" name="visibility" value="private" checked={visibility==='private'} onChange={()=>setVisibility('private')} style={{ cursor:'pointer' }} />
              <span style={{ fontSize:14 }}>Private (one user)</span>
            </label>
            {visibility==='private' && (
              <input 
                type="text" 
                placeholder="Enter user ID (UUID)" 
                value={privateUserId} 
                onChange={(e)=>setPrivateUserId(e.target.value)}
                style={{ marginTop:4, padding:'10px 12px', borderRadius:8, border:'1px solid var(--elev)', fontSize:14 }}
              />
            )}
          </div>
        </fieldset>

        <fieldset style={{ border:'1px solid var(--elev)', borderRadius:12, padding:16, display:'grid', gap:12 }}>
          <legend style={{ fontWeight:500, fontSize:14, padding:'0 8px' }}>Tags (Optional)</legend>
          <div style={{ display:'grid', gap:12 }}>
            <label style={{ display:'grid', gap:6 }}>
              <span style={{ fontSize:13, color:'var(--muted)' }}>Grade (e.g., K1, K2, K3)</span>
              <input 
                type="text" 
                value={tags.grade} 
                onChange={(e)=>setTags({...tags, grade: e.target.value})}
                placeholder="K1" 
                style={{ padding:'8px 12px', borderRadius:8, border:'1px solid var(--elev)', fontSize:14 }}
              />
            </label>
            <label style={{ display:'grid', gap:6 }}>
              <span style={{ fontSize:13, color:'var(--muted)' }}>Category (e.g., clothing, food, animals)</span>
              <input 
                type="text" 
                value={tags.category} 
                onChange={(e)=>setTags({...tags, category: e.target.value})}
                placeholder="food" 
                style={{ padding:'8px 12px', borderRadius:8, border:'1px solid var(--elev)', fontSize:14 }}
              />
            </label>
            <label style={{ display:'grid', gap:6 }}>
              <span style={{ fontSize:13, color:'var(--muted)' }}>Author (e.g., Super Simple Songs)</span>
              <input 
                type="text" 
                value={tags.author} 
                onChange={(e)=>setTags({...tags, author: e.target.value})}
                placeholder="Super Simple Songs" 
                style={{ padding:'8px 12px', borderRadius:8, border:'1px solid var(--elev)', fontSize:14 }}
              />
            </label>
          </div>
        </fieldset>

        {(error || message) && (
          <div style={{ display:'grid', gap:8 }}>
            {error && <div style={{ padding:'12px 16px', borderRadius:8, background:'#dc26261a', color:'#dc2626', fontSize:14, border:'1px solid #dc262633' }}>{error}</div>}
            {message && <div style={{ padding:'12px 16px', borderRadius:8, background:'#16a34a1a', color:'#16a34a', fontSize:14, border:'1px solid #16a34a33' }}>{message}</div>}
          </div>
        )}

        <button 
          type="submit" 
          disabled={submitting}
          style={{ 
            padding:'12px 24px', 
            borderRadius:8, 
            border:'none', 
            background: submitting ? 'var(--muted)' : 'var(--btn-bg)', 
            color: submitting ? '#888' : 'var(--btn-fg)', 
            fontSize:15, 
            fontWeight:600, 
            cursor: submitting ? 'not-allowed' : 'pointer',
            transition:'all 0.15s'
          }}
        >
          {submitting ? 'Uploading…' : 'Start upload'}
        </button>

        <div style={{ fontSize:12, color:'var(--muted)', textAlign:'center', paddingTop:8 }}>
          Selected resolutions will be transcoded in the background. You'll be notified when processing completes.
        </div>
      </form>
    </div>
  )
}
